import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Bike, Store, Handshake, ArrowUpRight, ArrowDown } from "lucide-react";
import { MaskedLines, FadeIn, Reveal, SectionHead, EASE } from "@/components/Motion";
import EditorialMarquee from "@/components/EditorialMarquee";
import RouteMap from "@/components/RouteMap";
import VideoFeature from "@/components/VideoFeature";
import CTA from "@/components/CTA";
import { IMAGES, MANIFESTO, PARTNERS } from "@/lib/site";
import CountdownRibbon from "@/components/CountdownRibbon";

const PORTAL_URL = process.env.REACT_APP_PORTAL_URL;
const EXPLORE_URL = process.env.REACT_APP_EXPLORE_URL;

const AUDIENCES = [
  {
    id: "riders",
    icon: Bike,
    to: "/riders",
    title: "Riders",
    body: "Beautiful bike paths paired with the cafés, breweries, and views worth pedaling to. Approved stops only — never noise.",
    cta: "See the rider experience",
    cls: "bg-[#0B2B22] text-[#F4F7F4]",
    iconCls: "bg-[#7FB998]/20 text-[#7FB998]",
  },
  {
    id: "businesses",
    icon: Store,
    to: "/businesses",
    title: "Local Businesses",
    body: "Put your shop on the route cyclists actually ride — Founding Advertiser pricing is $99/year per location, with pin placement in the Rider app included.",
    cta: "Advertise your business",
    cls: "bg-[#E05A47] text-white",
    iconCls: "bg-white/20 text-white",
  },
  {
    id: "rental-shops",
    icon: Handshake,
    to: "/partners",
    title: "Partners",
    body: "Bike shops, rental operators, cycling clubs, trail advocates, and tourism groups — become a Founding Partner. A $99 one-time setup includes your first zone and first year, with 50% of every advertiser on your routes.",
    cta: "Partner with VeloPaths",
    cls: "bg-white text-[#0B2B22] border border-[#154234]/15",
    iconCls: "bg-[#2D6A4F]/10 text-[#2D6A4F]",
  },
];

const STATS = [
  { k: "06", v: "Curated categories" },
  { k: "100%", v: "Vetted local stops" },
  { k: "02", v: "Platforms — iOS & Android" },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.02, 1.15]);

  return (
    <main data-testid="page-home">
      <section
        ref={heroRef}
        data-testid="home-hero"
        className="relative flex min-h-screen items-end overflow-hidden bg-[#0B2B22]"
      >
        <motion.div style={{ y, scale }} className="absolute inset-0 will-change-transform">
          <img
            src={IMAGES.heroHome}
            alt="Two riders cruising a scenic paved bike path along the coast"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B2B22] via-[#0B2B22]/50 to-[#0B2B22]/20" />
        </motion.div>
        <div className="grain absolute inset-0" />

        <FadeIn
          delay={1.1}
          className="absolute bottom-28 right-10 z-10 hidden xl:block"
        >
          <div className="rounded-2xl border border-white/15 bg-white/10 px-5 py-4 backdrop-blur-xl">
            <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-[#7FB998]">
              41.4901° N · 71.3128° W
            </p>
            <p className="mt-1.5 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#F4F7F4]/80">
              <span className="pin-pulse inline-block size-2 rounded-full bg-[#E05A47]" />
              Mile 04 — Trailhead Rest Stop · Open 24/7
            </p>
          </div>
        </FadeIn>

        <div className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-20 pt-44 sm:px-10 sm:pb-28 lg:px-16">
          <FadeIn delay={0.05}>
            <p className="font-mono text-xs sm:text-sm font-bold uppercase tracking-[0.3em] text-[#7FB998]">
              Beautiful routes. Vetted stops. Local gems.
            </p>
          </FadeIn>
          <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-[#F4F7F4]">
            <MaskedLines
              lines={[
                "Explore Every Path.",
                <span key="l2">
                  Discover <span className="text-[#E05A47]">Every Stop.</span>
                </span>,
              ]}
            />
          </h1>
          <FadeIn delay={0.55}>
            <p className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-[#F4F7F4]/80">
              Connecting cyclists, local businesses, and cycling communities.
            </p>
          </FadeIn>
          <FadeIn delay={0.75} className="mt-9 flex flex-wrap items-center gap-3">
            <a
              href={EXPLORE_URL}
              data-testid="hero-explore"
              className="inline-flex items-center gap-2 rounded-full bg-[#E05A47] px-7 py-3.5 text-sm font-bold text-white shadow-lg transition-transform duration-300 hover:-translate-y-0.5 hover:bg-[#c94a39]"
            >
              Explore Bike Paths <ArrowUpRight size={15} />
            </a>
            <Link
              to="/about"
              data-testid="btn-about-app"
              className="inline-flex items-center gap-2 rounded-full border border-[#F4F7F4]/40 px-6 py-3 text-sm font-bold text-[#F4F7F4] backdrop-blur transition-colors duration-300 hover:bg-[#F4F7F4]/10"
            >
              About the App
            </Link>
          </FadeIn>
          <FadeIn delay={0.9} className="mt-8 flex flex-wrap items-center gap-4">
            <div className="flex shrink-0 gap-3">
              <figure className="rounded-xl bg-[#F4F7F4] p-1.5 shadow-lg">
                <img
                  src="/images/app-store-qr.png"
                  alt="QR code linking to VeloPaths on the App Store"
                  data-testid="app-store-qr"
                  className="size-16"
                />
                <figcaption className="mt-1 text-center font-mono text-[8px] font-bold uppercase tracking-[0.2em] text-[#0B2B22]/70">
                  iOS
                </figcaption>
              </figure>
              <figure className="rounded-xl bg-[#F4F7F4] p-1.5 shadow-lg">
                <img
                  src="/images/play-store-qr.png"
                  alt="QR code linking to VeloPaths on the Google Play Store"
                  data-testid="hero-play-qr"
                  className="size-16"
                />
                <figcaption className="mt-1 text-center font-mono text-[8px] font-bold uppercase tracking-[0.2em] text-[#0B2B22]/70">
                  Android
                </figcaption>
              </figure>
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2.5">
                <a
                  href="https://play.google.com/store/apps/details?id=com.velopaths.rider&hl=en"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-play-store"
                  aria-label="Get VeloPaths on Google Play"
                  className="inline-block transition-transform duration-300 hover:-translate-y-0.5"
                >
                  <img
                    src="/images/google-play-badge.png"
                    alt="Get it on Google Play"
                    className="h-12 w-auto"
                  />
                </a>
                <a
                  href="https://apps.apple.com/us/app/velopaths-rider/id6817941154"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-app-store-badge"
                  aria-label="Download VeloPaths on the App Store"
                  className="inline-block transition-transform duration-300 hover:-translate-y-0.5"
                >
                  <img
                    src="/images/app-store-badge.svg"
                    alt="Download on the App Store"
                    className="h-12 w-auto"
                  />
                </a>
              </div>
              <p className="mt-2 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#F4F7F4]/50">
                Now on the App Store & Google Play
              </p>
            </div>
          </FadeIn>
          <FadeIn delay={1.3} className="mt-16 hidden items-center gap-3 sm:flex">
            <span className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-[#F4F7F4]/50">
              Scroll
            </span>
            <span className="scroll-cue-line block h-10 w-px bg-[#E05A47]" />
            <ArrowDown size={13} className="text-[#F4F7F4]/50" />
          </FadeIn>
        </div>
      </section>

      <CountdownRibbon />

      <EditorialMarquee />

      <VideoFeature
        testid="section-home-video"
        overline="Watch"
        title="Every ride has a story."
        sub="Under two minutes on how VeloPaths helps cyclists discover new places, find hidden gems, and support the local spots that make a route worth riding."
        src="/videos/home-rider-story-web.mp4"
        poster="/videos/home-rider-story-poster.jpg"
        posterAlt="VeloPaths film — a rider on a waterfront bike path introducing Every Ride Has a Story"
        badge="VeloPaths · 2 min"
      />

      <section className="mx-auto max-w-7xl px-6 py-24 sm:px-10 sm:py-32 lg:px-16">
        <div className="grid items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <SectionHead
              overline="What VeloPaths does"
              title="A ride is never just a ride."
              sub="VeloPaths maps beautiful bike paths and layers them with the local stops that make a ride unforgettable — breweries, cafés, ice cream, attractions, and the practical stuff too. Every pin is approved. Every detour is worth it."
            />
            <Reveal delay={0.15} className="mt-12 grid grid-cols-3 gap-6">
              {STATS.map((s) => (
                <div key={s.v} className="border-t-2 border-[#E05A47] pt-4">
                  <p className="font-mono text-2xl sm:text-3xl font-bold text-[#0B2B22]">{s.k}</p>
                  <p className="mt-1.5 text-xs sm:text-sm text-[#52635B]">{s.v}</p>
                </div>
              ))}
            </Reveal>
          </div>
          <Reveal delay={0.2} className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-3 translate-x-5 translate-y-5 rounded-[2rem] bg-[#E05A47]/15" />
              <img
                src={IMAGES.cafe}
                alt="An artisan café stop along the route"
                loading="lazy"
                decoding="async"
                className="relative aspect-[4/5] w-full rounded-[2rem] object-cover shadow-2xl"
              />
              <div className="absolute bottom-5 left-5 rounded-xl bg-[#0B2B22]/85 px-4 py-3 backdrop-blur">
                <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#7FB998]">
                  Approved stop · Cafés
                </p>
                <p className="font-display text-base font-bold text-[#F4F7F4]">Switchback Café</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-24 sm:px-10 sm:pb-32 lg:px-16">
        <SectionHead
          overline="Who it's for"
          title="Built for everyone on the route."
          sub="One platform, three ways in — whether you ride the paths, serve the riders, or share the local knowledge that makes them worth riding."
        />
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {AUDIENCES.map((a, i) => (
            <Reveal key={a.id} delay={i * 0.12} className="h-full">
              <Link
                to={a.to}
                data-testid={`card-audience-${a.id}`}
                className={`group flex h-full flex-col rounded-[1.75rem] p-8 transition-transform duration-500 hover:-translate-y-2 ${a.cls}`}
                style={{ transitionTimingFunction: `cubic-bezier(${EASE.join(",")})` }}
              >
                <span className={`grid size-12 place-items-center rounded-2xl ${a.iconCls}`}>
                  <a.icon size={22} />
                </span>
                <h3 className="mt-6 font-display text-2xl font-bold tracking-tight">{a.title}</h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed opacity-75">{a.body}</p>
                <span className="mt-8 inline-flex items-center gap-2 text-sm font-bold">
                  {a.cta}
                  <ArrowUpRight
                    size={15}
                    className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                  />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section
        data-testid="section-founding-offer"
        className="mx-auto max-w-7xl px-6 pb-24 sm:px-10 sm:pb-32 lg:px-16"
      >
        <SectionHead
          center
          overline="Founding partner offer"
          title="Founding pricing, for a limited time."
          sub="Early adopters receive preferred pricing during the initial growth phase of the VeloPaths platform."
        />
        <div className="mt-14 grid gap-6 md:grid-cols-2">
          <Reveal className="h-full">
            <div
              data-testid="founding-card-partner"
              className="flex h-full flex-col rounded-[2rem] bg-[#0B2B22] p-8 text-[#F4F7F4] sm:p-10"
            >
              <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-[#7FB998]">
                Founding Partner
              </p>
              <p className="mt-4">
                <span className="font-mono text-5xl font-bold">$99</span>
                <span className="text-[#F4F7F4]/60"> one-time setup</span>
              </p>
              <ul className="mt-6 flex-1 space-y-3 text-sm leading-relaxed text-[#F4F7F4]/80">
                <li className="flex items-start gap-3">
                  <span className="mt-2 size-1.5 shrink-0 rounded-full bg-[#E05A47]" />
                  First zone included
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-2 size-1.5 shrink-0 rounded-full bg-[#E05A47]" />
                  One year of participation included
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-2 size-1.5 shrink-0 rounded-full bg-[#E05A47]" />
                  50/50 revenue share on your advertisers
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-2 size-1.5 shrink-0 rounded-full bg-[#E05A47]" />
                  Additional zones available for an additional fee — pricing varies by traffic and
                  revenue potential
                </li>
              </ul>
              <a
                href={PORTAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="btn-founding-partner"
                className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-[#E05A47] px-6 py-3.5 text-sm font-bold text-white transition-colors duration-300 hover:bg-[#c94a39]"
              >
                Become a Partner <ArrowUpRight size={15} />
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.12} className="h-full">
            <div
              data-testid="founding-card-advertiser"
              className="flex h-full flex-col rounded-[2rem] border border-[#154234]/12 bg-white p-8 sm:p-10"
            >
              <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-[#2D6A4F]">
                Founding Advertiser
              </p>
              <p className="mt-4">
                <span className="font-mono text-5xl font-bold text-[#0B2B22]">$99</span>
                <span className="text-[#52635B]"> / year · per location</span>
              </p>
              <ul className="mt-6 flex-1 space-y-3 text-sm leading-relaxed text-[#52635B]">
                <li className="flex items-start gap-3">
                  <span className="mt-2 size-1.5 shrink-0 rounded-full bg-[#2D6A4F]" />
                  Advertiser pin placement within an eligible zone
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-2 size-1.5 shrink-0 rounded-full bg-[#2D6A4F]" />
                  Listing visibility in the VeloPaths Rider app
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-2 size-1.5 shrink-0 rounded-full bg-[#2D6A4F]" />
                  Reach cyclists near bike paths and destinations
                </li>
              </ul>
              <a
                href={PORTAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="btn-founding-advertiser"
                className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-[#0B2B22] px-6 py-3.5 text-sm font-bold text-[#F4F7F4] transition-colors duration-300 hover:bg-[#154234]"
              >
                Reserve Your Location <ArrowUpRight size={15} />
              </a>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.15}>
          <p className="mx-auto mt-8 max-w-2xl text-center font-mono text-xs uppercase tracking-[0.2em] leading-relaxed text-[#52635B]">
            Renewal pricing may differ in future years as the platform expands and additional
            features are introduced.
          </p>
        </Reveal>
      </section>

      <section className="border-y border-[#154234]/10 bg-white py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-16">
          <Reveal className="text-center">
            <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-[#52635B]">
              Founding partners
            </p>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl font-bold tracking-tight text-[#0B2B22]">
              Trusted by trail-town pioneers.
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {PARTNERS.map((p, i) => {
              const card = (
                <div
                  data-testid={`partner-logo-${p.id}`}
                  className="flex h-36 flex-col items-center justify-center gap-3 rounded-2xl border border-[#154234]/10 bg-white p-5 text-center transition-all duration-300 hover:-translate-y-1 hover:border-[#2D6A4F]/40 hover:shadow-lg"
                >
                  {p.logo ? (
                    <img
                      src={p.logo}
                      alt={`${p.name} logo`}
                      className="h-14 w-auto max-w-[85%] object-contain"
                    />
                  ) : (
                    <span
                      className="grid size-10 place-items-center rounded-full font-display text-sm font-black text-white"
                      style={{ backgroundColor: p.color }}
                    >
                      {p.name
                        .split(" ")
                        .filter((w) => /^[A-Z]/.test(w))
                        .slice(0, 2)
                        .map((w) => w[0])
                        .join("")}
                    </span>
                  )}
                  <div>
                    <p
                      className="font-display text-base font-bold tracking-tight"
                      style={{ color: p.color }}
                    >
                      {p.name}
                    </p>
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-[#52635B]">
                      {p.tag}
                    </p>
                  </div>
                </div>
              );
              return (
                <Reveal key={p.id} delay={i * 0.08} className="h-full">
                  {p.url ? (
                    <a
                      href={p.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`partner-link-${p.id}`}
                      className="block h-full"
                    >
                      {card}
                    </a>
                  ) : (
                    card
                  )}
                </Reveal>
              );
            })}
            <Reveal delay={0.24} className="h-full">
              <Link
                to="/businesses"
                data-testid="partner-logo-invite"
                className="group flex h-36 flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-[#154234]/25 p-5 text-center transition-colors duration-300 hover:border-[#E05A47]"
              >
                <span className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#52635B]">
                  Your logo here
                </span>
                <span className="flex items-center justify-between gap-2">
                  <span className="font-display text-lg font-bold tracking-tight text-[#0B2B22]">
                    Become a founding partner
                  </span>
                  <ArrowUpRight
                    size={16}
                    className="shrink-0 text-[#E05A47] transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                  />
                </span>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-[#0B2B22] py-24 text-[#F4F7F4] sm:py-32">
        <div className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-16">
          <SectionHead
            dark
            overline="Inside the app"
            title="One map. Only the good stuff."
            sub="This is a live preview of the VeloPaths map experience. Filter by category, tap a pin, and see how curated stops replace the clutter of generic maps."
          />
          <Reveal delay={0.15} className="mt-12">
            <RouteMap />
          </Reveal>
          <Reveal delay={0.25} className="mt-10 flex justify-center">
            <a
              href={EXPLORE_URL}
              data-testid="home-explore-map"
              className="inline-flex items-center gap-2 rounded-full bg-[#E05A47] px-8 py-4 text-sm font-bold text-white shadow-xl transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#c94a39]"
            >
              Open the Live Trail Map <ArrowUpRight size={16} />
            </a>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 sm:px-10 sm:py-32 lg:px-16">
        <SectionHead overline="The VeloPaths manifesto" title="What we ride by." />
        <div className="mt-14 grid gap-x-14 gap-y-12 md:grid-cols-2">
          {MANIFESTO.map((m, i) => (
            <Reveal key={m.n} delay={i * 0.1}>
              <div className="border-t border-[#154234]/20 pt-7">
                <p className="font-mono text-sm font-bold text-[#E05A47]">{m.n}</p>
                <h3 className="mt-3 font-display text-xl sm:text-2xl font-bold tracking-tight text-[#0B2B22]">
                  {m.title}
                </h3>
                <p className="mt-3 text-sm sm:text-base leading-relaxed text-[#52635B]">{m.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <CTA
        overline="Ready when you are"
        title="Ready to ride the route less boring?"
        sub="Start exploring live bike paths right now — or put your business on the map and bring VeloPaths to your town."
        primary={{ label: "Explore the Trails", to: EXPLORE_URL, sameTab: true, testid: "btn-cta-explore-app" }}
        secondary={{ label: "Talk to us", to: "/contact", testid: "btn-cta-talk-to-us" }}
      />
    </main>
  );
}
